import React, { useState } from 'react';

// Inline SVG Icons
const Icons = {
  HelpCircle: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Book: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  MessageSquare: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Mail: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  ChevronDown: ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ChevronRight: ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  Send: ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  ExternalLink: ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
};

const HelpSupport: React.FC = () => {
  const [activeTab, setActiveTab] = useState('faqs');
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const tabs = [
    { id: 'faqs', label: 'FAQs', icon: <Icons.HelpCircle size={18} /> },
    { id: 'documentation', label: 'Documentation', icon: <Icons.Book size={18} /> },
    { id: 'contact', label: 'Contact Us', icon: <Icons.MessageSquare size={18} /> },
  ];

  const faqs = [
    {
      id: '1',
      category: 'Getting Started',
      question: 'How do I create my first email campaign?',
      answer: 'To create your first campaign, navigate to the Campaigns page and click "New Campaign". You\'ll need to select a template, choose your sender profile, select recipients from your contacts, and schedule or send immediately.',
    },
    {
      id: '2',
      category: 'Getting Started',
      question: 'How do I import contacts?',
      answer: 'Go to the Contacts page and click "Import Contacts". You can upload a CSV file with your contact list. Make sure your CSV includes columns for email, name, and any other relevant information.',
    },
    {
      id: '3',
      category: 'Sender Profiles',
      question: 'What is a sender profile?',
      answer: 'A sender profile contains your SMTP/IMAP email configuration. It allows you to send emails through your own email provider (like Gmail, Outlook, or custom SMTP). You can set up multiple profiles for different email addresses.',
    },
    {
      id: '4',
      category: 'Sender Profiles',
      question: 'How do I set up Gmail for sending emails?',
      answer: 'For Gmail, you\'ll need to create an App Password at https://myaccount.google.com/apppasswords. Use this app password in your sender profile instead of your regular Gmail password. Enable 2-factor authentication on your Gmail account first.',
    },
    {
      id: '5',
      category: 'Inbox',
      question: 'How does the IMAP inbox feature work?',
      answer: 'The IMAP inbox feature allows you to view and manage emails from your connected email accounts directly within MailFlow. Enable IMAP in your sender profile configuration with the correct IMAP settings for your email provider.',
    },
    {
      id: '6',
      category: 'Tracking',
      question: 'How are email opens and clicks tracked?',
      answer: 'MailFlow uses tracking pixels for opens and redirect links for clicks. These are automatically added to your emails when sent through the platform. You can view detailed analytics in the Campaigns page.',
    },
    {
      id: '7',
      category: 'Troubleshooting',
      question: 'Why are my emails going to spam?',
      answer: 'Emails may go to spam due to sender reputation, content issues, or authentication problems. Ensure your domain has SPF, DKIM, and DMARC records configured. Use a reputable sender profile and avoid spam-like content.',
    },
    {
      id: '8',
      category: 'Troubleshooting',
      question: 'How do I handle bounced emails?',
      answer: 'Bounced emails are automatically detected. In Settings, you can configure bounce handling to mark contacts as bounced, unsubscribe them, or delete them from your list. Regularly clean your contact list to maintain deliverability.',
    },
  ];

  const documentationSections = [
    {
      title: 'Quick Start Guide',
      items: [
        'Setting up your account',
        'Creating sender profiles',
        'Importing your first contacts',
        'Sending your first campaign',
      ],
    },
    {
      title: 'Campaign Management',
      items: [
        'Creating campaigns',
        'Using templates',
        'Scheduling sends',
        'A/B testing',
      ],
    },
    {
      title: 'Contact Management',
      items: [
        'Importing contacts',
        'Managing lists',
        'Segmentation',
        'Unsubscribe handling',
      ],
    },
    {
      title: 'Analytics & Reporting',
      items: [
        'Open rates',
        'Click tracking',
        'Bounce analysis',
        'Export reports',
      ],
    },
  ];

  const handleSubmitContact = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Support request submitted! We\'ll get back to you within 24 hours.');
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>Help & Support</h1>
        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Find answers to common questions or get in touch with our support team</p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '4px', 
        marginBottom: '24px', 
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 16px',
              background: activeTab === tab.id ? '#eff6ff' : 'transparent',
              color: activeTab === tab.id ? '#2563eb' : '#6b7280',
              border: activeTab === tab.id ? '1px solid #bfdbfe' : '1px solid transparent',
              borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab.id ? 600 : 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 150ms ease-out',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'faqs' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '32px' }}>
          {/* FAQs */}
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                style={{
                  borderBottom: index < faqs.length - 1 ? '1px solid #e5e7eb' : 'none',
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    background: expandedFaq === faq.id ? '#f9fafb' : 'white',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 150ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    if (expandedFaq !== faq.id) {
                      e.currentTarget.style.background = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = expandedFaq === faq.id ? '#f9fafb' : 'white';
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: '#eef2ff',
                        color: '#6366f1',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '8px',
                      }}
                    >
                      {faq.category}
                    </span>
                    <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827', margin: '8px 0 0 0' }}>
                      {faq.question}
                    </h3>
                  </div>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#64748b',
                      transform: expandedFaq === faq.id ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 150ms ease-out',
                      flexShrink: 0,
                    }}
                  >
                    <Icons.ChevronDown size={16} />
                  </div>
                </button>
                {expandedFaq === faq.id && (
                  <div style={{ padding: '0 24px 20px 24px' }}>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Quick Links */}
          <div>
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '24px', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
                Quick Links
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6366f1',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <Icons.Book size={16} />
                  Full Documentation
                  <Icons.ExternalLink size={12} />
                </button>
                <button
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6366f1',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <Icons.MessageSquare size={16} />
                  Community Forum
                  <Icons.ExternalLink size={12} />
                </button>
                <button
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6366f1',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <Icons.Mail size={16} />
                  Email Support
                  <Icons.ExternalLink size={12} />
                </button>
              </div>
            </div>

            <div style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', 
              borderRadius: '12px', 
              padding: '24px',
              color: 'white'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                Still need help?
              </h3>
              <p style={{ fontSize: '0.875rem', marginBottom: '16px', opacity: 0.9 }}>
                Our support team is available 24/7 to assist you with any questions.
              </p>
              <button
                onClick={() => setActiveTab('contact')}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'white',
                  color: '#6366f1',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'all 150ms ease-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'documentation' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
          {documentationSections.map((section, index) => (
            <div
              key={index}
              style={{ 
                background: 'white', 
                borderRadius: '12px', 
                padding: '24px', 
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#6366f1' }}><Icons.Book size={20} /></span>
                {section.title}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {section.items.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: '12px' }}>
                    <button
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#6366f1',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        transition: 'gap 150ms ease-out',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.gap = '12px';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.gap = '8px';
                      }}
                    >
                      <Icons.ChevronRight size={14} />
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'contact' && (
        <div style={{ maxWidth: '600px' }}>
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '32px', 
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
              Contact Support
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '24px' }}>
              Fill out the form below and our team will get back to you within 24 hours.
            </p>

            <form onSubmit={handleSubmitContact}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Message
                </label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  rows={6}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 150ms ease-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Icons.Send size={18} />
                Send Message
              </button>
            </form>

            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                Other ways to reach us
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <a
                  href="mailto:support@mailflow.com"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6366f1',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                  }}
                >
                  <Icons.Mail size={16} />
                  support@mailflow.com
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpSupport;
